import { useApiClient } from '@klareospace/pharos-lib-front-utils';
import type { Task } from '@klareospace/pharos-lib-types';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { createMutationCallbacks } from '@klareospace/pharos-lib-web-utils';

import { orderKeys } from '&/features/orders/api';

import { createTasksApi, taskKeys } from '../api';

// ─── Hook ─────────────────────────────────────────────────────────────────────

/** Wraps the existing POST /tasks/:id/reservation route for the restricted `tasks:self-assign` path. */
export function useSelfAssignTask(orderId: string): UseMutationResult<Task, Error, string> {
  const client = useApiClient('deliveries');
  const api = createTasksApi(client);
  const queryClient = useQueryClient();
  const { t } = useTranslation('tasks');

  return useMutation<Task, Error, string>({
    mutationFn: (taskId: string) => api.reserveTask(taskId),
    ...createMutationCallbacks({
      errorKey: 'assignment.errors.selfAssignFailed',
      invalidateKeys: (taskId: string) => [
        orderKeys.tasks(orderId),
        orderKeys.detail(orderId),
        orderKeys.history(orderId),
        orderKeys.lists(),
        taskKeys.history(taskId),
      ],
      queryClient,
      successKey: 'assignment.success.assigned',
      t,
    }),
  });
}
